/**
 * SurfaceSwatch — one card *surface* on the hidden /test page.
 *
 * Deliberately contentless. The swatch is an empty box wearing nothing but the
 * chrome under test — background, border, radius, shadow — so two surfaces can
 * be compared without their contents arguing for attention.
 *
 * Two ways to declare one:
 *   • `surfaceSx` — for chrome written inline in a tab. The spec list is derived
 *     straight from the sx, so the swatch and its documentation can't drift.
 *   • `children` — for chrome owned by a component (render the real component
 *     around an empty spacer). Pass `spec` yourself, since the styles live in
 *     the component rather than here.
 */
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";

const MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

/** Height of an empty swatch — tall enough to read a shadow, short enough to grid. */
const SWATCH_HEIGHT = 78;

/**
 * Pull the four chrome properties out of an `sx` block, in a fixed order, so
 * every spec list reads the same way. Anything absent is reported as such —
 * "no shadow" is itself a design decision worth stating.
 */
const specFromSx = (sx = {}) => [
  ["background", sx.background || sx.bgcolor || sx.backgroundColor || "—"],
  ["border", sx.border || "none"],
  [
    "radius",
    typeof sx.borderRadius === "number"
      ? `${sx.borderRadius} (${sx.borderRadius * 8}px)`
      : sx.borderRadius || "0",
  ],
  ["shadow", sx.boxShadow || "none"],
];

const SpecList = memo(({ rows }) => (
  <Flx column gap={0.25}>
    {rows.map(([key, value]) => (
      <Flx key={key} gap={1} sx={{ alignItems: "baseline" }}>
        <Txt
          sx={{
            flexShrink: 0,
            width: 66,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "#94a3b8",
          }}
        >
          {key}
        </Txt>
        <Txt
          sx={{
            fontFamily: MONO,
            fontSize: 10.5,
            lineHeight: 1.45,
            color: value === "none" || value === "—" ? "#b6c2cf" : "#334155",
            wordBreak: "break-word",
          }}
        >
          {value}
        </Txt>
      </Flx>
    ))}
  </Flx>
));

const SurfaceSwatch = memo(
  ({ label, note, spec, surfaceSx, height = SWATCH_HEIGHT, children }) => {
    const rows = useMemo(
      () => spec || specFromSx(surfaceSx),
      [spec, surfaceSx],
    );

    return (
      <Flx column gap={1.25} sx={{ minWidth: 0 }}>
        <Flx column gap={0.25}>
          <Txt sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
            {label}
          </Txt>
          {note && (
            <Txt sx={{ fontSize: 11, color: "#64748b" }}>{note}</Txt>
          )}
        </Flx>

        {children ? (
          <Box sx={{ "& > *": { margin: "0 !important" } }}>{children}</Box>
        ) : (
          <Box sx={{ height, ...surfaceSx }} />
        )}

        <SpecList rows={rows} />
      </Flx>
    );
  },
);

/** Empty filler for component-owned swatches — gives the surface something to size to. */
export const SwatchFill = ({ height = SWATCH_HEIGHT }) => (
  <Box sx={{ height }} />
);

SurfaceSwatch.propTypes = {
  /** Variant name — "default", "selected", "alert", … */
  label: PropTypes.string.isRequired,
  /** Optional one-liner about when this variant applies. */
  note: PropTypes.node,
  /** Explicit spec rows as [key, value] pairs. Required when using `children`. */
  spec: PropTypes.arrayOf(PropTypes.array),
  /** The chrome under test, for inline patterns. Spec is derived from it. */
  surfaceSx: PropTypes.object,
  height: PropTypes.number,
  /** Render the real component here instead of an sx-driven box. */
  children: PropTypes.node,
};

SurfaceSwatch.displayName = "SurfaceSwatch";

export default SurfaceSwatch;
